import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a paginated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;
    this.#animateLayoutChange(target.value);
  }

  /**
   * Animates and applies the layout change.
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;
    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Sets the layout attribute and updates --mobile-columns CSS variable.
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;

    grid.setAttribute('product-grid-view', value);

    // Update --mobile-columns inline so it overrides the hardcoded inline style
    const isMobile = !mediaQueryLarge.matches;
    if (isMobile) {
      if (value === 'mobile-single' || value === 'mobile-list') {
        grid.style.setProperty('--mobile-columns', '1');
      } else {
        // 'default'
        grid.style.setProperty('--mobile-columns', '2');
      }
    }
  }

  /**
   * Handles the media query change event (mobile <-> desktop switch).
   * Restores the saved layout for the new viewport instead of always resetting to 'default'.
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const isDesktop = event.matches;
    const viewport = isDesktop ? 'desktop' : 'mobile';

    // Restore the saved layout for the new viewport
    const savedLayout = sessionStorage.getItem(`product-grid-view-${viewport}`) || 'default';

    // Sync the correct radio button
    const layoutAttr = isDesktop ? savedLayout : savedLayout;
    const targetElement = this.querySelector(
      `input[type="radio"][value="${layoutAttr}"]`
    );

    // Fallback: if no matching radio found, check the default option
    if (targetElement instanceof HTMLInputElement) {
      targetElement.checked = true;
    } else {
      const defaultEl = isDesktop
        ? this.querySelector('[data-grid-layout="desktop-default-option"]')
        : this.querySelector('[data-grid-layout="mobile-option"]');
      if (defaultEl instanceof HTMLInputElement) defaultEl.checked = true;
    }

    this.#setLayout(savedLayout);
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
